/**
 * CurateOverlay - Form for creating curated branches from selected projects
 * 
 * A curated branch is a collection of related projects assembled by a curator.
 * This is different from forking - curation is about organizing existing work,
 * not creating derivatives.
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, FolderTree, Eye, EyeOff, Users, Lock, Globe } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button, Input, Textarea, Badge, Avatar } from '../ui';
import { projectsAPI, api } from '../../api';

export const CurateOverlay = ({
  selectedProjects = [],
  onClose,
  onSuccess
}) => {
  const overlayRef = useRef(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  
  // Validate form
  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Branch name is required';
    if (name.length > 100) errs.name = 'Name must be under 100 characters';
    if (selectedProjects.length < 2) errs.projects = 'Select at least 2 projects to curate';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // Create curated branch via API
      const payload = {
        name: name.trim(),
        description: description.trim(),
        is_public: isPublic,
        project_ids: selectedProjects.map(p => p.id)
      };
      const response = await (projectsAPI.createCuratedBranch
        ? projectsAPI.createCuratedBranch(payload)
        : api.post('/projects/curated-branches', payload));
      
      onSuccess?.(response.data);
    } catch (err) {
      console.error('Failed to create curated branch:', err);
      setErrors({ submit: err.response?.data?.error || 'Failed to create curated branch' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        ref={overlayRef}
        className="w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-xl">
              <FolderTree className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Create Curated Branch</h2>
              <p className="text-sm text-zinc-400">Organize selected projects into a collection</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Selected Projects Preview */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Projects to Curate ({selectedProjects.length})
              </label>
              <div className="flex flex-wrap gap-2 p-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                {selectedProjects.map(project => (
                  <div
                    key={project.id}
                    className="flex items-center gap-2 px-3 py-1.5 bg-zinc-700/50 rounded-lg"
                  >
                    {(project.imageUrl || project.image_url) ? (
                      <img
                        src={project.imageUrl || project.image_url}
                        alt={project.name}
                        className="w-6 h-6 rounded object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded bg-zinc-600 flex items-center justify-center text-xs text-zinc-300">
                        {project.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm text-zinc-200 truncate max-w-[120px]">
                      {project.name}
                    </span>
                  </div>
                ))}
              </div>
              {errors.projects && (
                <p className="mt-1 text-sm text-red-400">{errors.projects}</p>
              )}
            </div>
            
            {/* Branch Name */}
            <Input
              label="Branch Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Give your curated branch a name"
              error={errors.name}
              required
            />
            
            {/* Description */}
            <Textarea
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this collection represents..."
              rows={3}
            />
            
            {/* Visibility Toggle */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-3">Visibility</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setIsPublic(true)}
                  className={cn(
                    'flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left',
                    isPublic
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  )}
                >
                  <Globe className={cn('w-5 h-5', isPublic ? 'text-emerald-400' : 'text-zinc-400')} />
                  <div>
                    <p className={cn('font-medium', isPublic ? 'text-emerald-400' : 'text-zinc-300')}>
                      Public
                    </p>
                    <p className="text-xs text-zinc-500">Anyone can view</p>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setIsPublic(false)}
                  className={cn(
                    'flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left',
                    !isPublic
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  )}
                >
                  <Lock className={cn('w-5 h-5', !isPublic ? 'text-purple-400' : 'text-zinc-400')} />
                  <div>
                    <p className={cn('font-medium', !isPublic ? 'text-purple-400' : 'text-zinc-300')}>
                      Private
                    </p>
                    <p className="text-xs text-zinc-500">Only you can view</p>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Info about curation */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <div className="flex gap-3">
                <Users className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-200 font-medium">About Curated Branches</p>
                  <p className="text-xs text-amber-200/70 mt-1">
                    Curating creates a named collection of projects. The original creators retain 
                    ownership of their work. If this branch generates revenue, earnings are shared 
                    with all contributors.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Error message */}
            {errors.submit && (
              <p className="text-sm text-red-400 text-center">{errors.submit}</p>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-800 bg-zinc-900/50">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting} icon={FolderTree}>
              Create Branch
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CurateOverlay;